import { ApiError, apiRequest } from "./apiClient";
import {
  parseSectionAiResults,
  parseSectionAiSelections,
} from "../utils/generateFlowAi";
import type {
  FieldStep,
  KbGenerationSessionAiStartResult,
  KbGenerationSessionAiStatus,
  KbGenerationSessionCommitResult,
  KbGenerationSessionDetail,
  KbGenerationSessionListItem,
  KbGenerationSessionPatch,
  KbGenerationSessionPreviewResult,
  KbGenerationSessionResolvePlaceholdersResult,
  KbSectionListItem,
  ListKbSectionsParams,
  ListKbSectionsResult,
} from "../types/gameRuleKbGenerate";

export const KB_GENERATION_SESSION_STORAGE_KEY = "kb-gen-session-v1";

type SuccessResponse<T> = {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
};

type KbSectionListApiItem = {
  readonly id: string;
  readonly name: string;
  readonly description?: string | null;
  readonly primary_tag?: string | null;
  readonly field_count?: number;
  readonly status?: string;
  readonly version?: number;
  readonly section_group_id?: string;
};

type KbSectionListApiResponse = {
  readonly success: boolean;
  readonly data: readonly KbSectionListApiItem[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
  readonly message?: string;
};

type KbGenerationSessionApiDetail = {
  readonly id: string;
  readonly rule_name?: string | null;
  readonly intent_text?: string | null;
  readonly file_name?: string | null;
  readonly current_step?: number;
  readonly status?: string;
  readonly selected_section_ids?: readonly string[];
  readonly section_order?: readonly string[];
  readonly programme?: Record<string, string> | null;
  readonly field_values?: Record<string, unknown> | null;
  readonly ai_results?: Record<string, unknown> | null;
  readonly ai_selections?: Record<string, unknown> | null;
  readonly reviewer_email?: string | null;
  readonly reviewer_notes?: string | null;
  readonly template_id?: string | null;
  readonly content_mode?: string;
  readonly flow_type?: string;
};

type KbGenerationSessionListApiItem = {
  readonly id: string;
  readonly rule_name?: string | null;
  readonly current_step?: number;
  readonly status?: string;
  readonly updated_at: string;
  readonly selected_section_count?: number;
  readonly ai_completed_count?: number;
};

type KbGenerationSessionAiStartApi = {
  readonly session_id: string;
  readonly ai_job_id: string;
  readonly status: string;
  readonly total_sections: number;
  readonly completed_sections: number;
};

type KbGenerationSessionAiStatusApi = {
  readonly session_id: string;
  readonly status: string;
  readonly ai_job_id?: string | null;
  readonly progress?: {
    readonly total?: number;
    readonly completed?: number;
    readonly current_section_id?: string | null;
  } | null;
  readonly ai_results?: Record<string, unknown> | null;
  readonly error?: string | null;
};

type FieldStepApiField = {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly required?: boolean;
  readonly value?: string;
  readonly dropdown_options?: readonly string[] | null;
  readonly doc_placeholder?: string | null;
  readonly hint?: string | null;
};

type FieldStepApiItem = {
  readonly id: string;
  readonly name: string;
  readonly tag?: string | null;
  readonly description?: string | null;
  readonly content?: string | null;
  readonly fields?: readonly FieldStepApiField[];
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
    return raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

const mapSectionListItem = (row: KbSectionListApiItem): KbSectionListItem => ({
  id: String(row.id),
  name: row.name,
  description: row.description ?? null,
  primaryTag: row.primary_tag ?? null,
  fieldCount: row.field_count ?? 0,
  status: row.status ?? "active",
  version: row.version,
  sectionGroupId: row.section_group_id,
});

const mapSessionListItem = (
  row: KbGenerationSessionListApiItem,
): KbGenerationSessionListItem => ({
  id: String(row.id),
  ruleName: row.rule_name ?? null,
  currentStep: row.current_step ?? 1,
  status: row.status ?? "draft",
  updatedAt: row.updated_at,
  selectedSectionCount: row.selected_section_count ?? 0,
  aiCompletedCount: row.ai_completed_count ?? 0,
});

const mapSessionDetail = (row: KbGenerationSessionApiDetail): KbGenerationSessionDetail => ({
  id: String(row.id),
  ruleName: row.rule_name ?? null,
  intentText: row.intent_text ?? null,
  fileName: row.file_name ?? null,
  currentStep: row.current_step ?? 1,
  status: row.status ?? "draft",
  selectedSectionIds: Array.isArray(row.selected_section_ids)
    ? row.selected_section_ids.map(String)
    : [],
  sectionOrder: Array.isArray(row.section_order) ? row.section_order.map(String) : [],
  programme: row.programme ?? null,
  fieldValues: row.field_values ?? null,
  aiResults: parseSectionAiResults(row.ai_results),
  aiSelections: parseSectionAiSelections(row.ai_selections),
  reviewerEmail: row.reviewer_email ?? null,
  reviewerNotes: row.reviewer_notes ?? null,
  templateId: row.template_id ? String(row.template_id) : null,
  contentMode: row.content_mode ?? "ai_suggestions",
  flowType: row.flow_type ?? "manual",
});

const mapAiStatus = (row: KbGenerationSessionAiStatusApi): KbGenerationSessionAiStatus => {
  const progressRaw = row.progress;
  const progress =
    progressRaw && typeof progressRaw === "object"
      ? {
          total: typeof progressRaw.total === "number" ? progressRaw.total : 0,
          completed:
            typeof progressRaw.completed === "number" ? progressRaw.completed : 0,
          currentSectionId:
            typeof progressRaw.current_section_id === "string"
              ? progressRaw.current_section_id
              : null,
        }
      : null;
  return {
    sessionId: String(row.session_id),
    status: row.status,
    aiJobId: row.ai_job_id ? String(row.ai_job_id) : null,
    progress,
    aiResults: parseSectionAiResults(row.ai_results),
    error: row.error ?? null,
  };
};

const mapFieldStep = (step: FieldStepApiItem): FieldStep => ({
  id: step.id,
  name: step.name,
  tag: step.tag ?? null,
  description: step.description ?? null,
  content: step.content ?? null,
  fields: (step.fields ?? []).map((field) => ({
    key: field.key,
    label: field.label,
    type: field.type,
    required: Boolean(field.required),
    value: field.value ?? "",
    dropdownOptions: field.dropdown_options ?? null,
    docPlaceholder: field.doc_placeholder ?? null,
    hint: field.hint ?? null,
  })),
});

const patchToApiBody = (patch: KbGenerationSessionPatch): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  if (patch.currentStep !== undefined) {
    body.current_step = patch.currentStep;
  }
  if (patch.ruleName !== undefined) {
    body.rule_name = patch.ruleName;
  }
  if (patch.intentText !== undefined) {
    body.intent_text = patch.intentText;
  }
  if (patch.fileName !== undefined) {
    body.file_name = patch.fileName;
  }
  if (patch.selectedSectionIds !== undefined) {
    body.selected_section_ids = patch.selectedSectionIds;
  }
  if (patch.sectionOrder !== undefined) {
    body.section_order = patch.sectionOrder;
  }
  if (patch.programme !== undefined) {
    body.programme = patch.programme;
  }
  if (patch.fieldValues !== undefined) {
    body.field_values = patch.fieldValues;
  }
  if (patch.aiSelections !== undefined) {
    body.ai_selections = Object.fromEntries(
      Object.entries(patch.aiSelections).map(([sectionId, selection]) => {
        const entry: Record<string, unknown> = {
          selected_index: selection.selectedIndex,
          final_content: selection.finalContent,
          was_edited: selection.wasEdited,
        };
        if (selection.finalStructure && typeof selection.finalStructure === "object") {
          entry.final_structure = selection.finalStructure;
        }
        return [sectionId, entry];
      }),
    );
  }
  if (patch.reviewerEmail !== undefined) {
    body.reviewer_email = patch.reviewerEmail;
  }
  if (patch.reviewerNotes !== undefined) {
    body.reviewer_notes = patch.reviewerNotes;
  }
  if (patch.status !== undefined) {
    body.status = patch.status;
  }
  if (patch.templateId !== undefined) {
    body.template_id = patch.templateId;
  }
  if (patch.contentMode !== undefined) {
    body.content_mode = patch.contentMode;
  }
  if (patch.flowType !== undefined) {
    body.flow_type = patch.flowType;
  }
  return body;
};

export const readStoredGenerationSessionId = (): string | null => {
  try {
    const raw = sessionStorage.getItem(KB_GENERATION_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { sessionId?: string };
    return typeof parsed.sessionId === "string" && parsed.sessionId.trim()
      ? parsed.sessionId.trim()
      : null;
  } catch {
    return null;
  }
};

export const persistGenerationSessionId = (sessionId: string): void => {
  sessionStorage.setItem(
    KB_GENERATION_SESSION_STORAGE_KEY,
    JSON.stringify({ sessionId }),
  );
};

export const clearGenerationSessionId = (): void => {
  try {
    sessionStorage.removeItem(KB_GENERATION_SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const listOpenGenerationSessions = async (
  limit = 100,
): Promise<readonly KbGenerationSessionListItem[]> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions?limit=${Math.max(1, Math.min(limit, 100))}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<readonly KbGenerationSessionListApiItem[]>;
  const rows = Array.isArray(json.data) ? json.data : [];
  return rows.map(mapSessionListItem);
};

export const abandonGenerationSession = async (sessionId: string): Promise<void> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
};

export type CreateGenerationSessionOptions = {
  readonly templateId?: string;
  readonly flowType?: string;
  readonly contentMode?: string;
  readonly selectedSectionIds?: readonly string[];
  readonly sectionOrder?: readonly string[];
};

export const createGenerationSession = async (
  ruleName: string,
  intentText: string,
  options?: CreateGenerationSessionOptions,
): Promise<KbGenerationSessionDetail> => {
  const body: Record<string, unknown> = {
    rule_name: ruleName.trim(),
    intent_text: intentText.trim(),
  };
  if (options?.templateId) {
    body.template_id = options.templateId;
  }
  if (options?.flowType) {
    body.flow_type = options.flowType;
  }
  if (options?.contentMode) {
    body.content_mode = options.contentMode;
  }
  if (options?.selectedSectionIds?.length) {
    body.selected_section_ids = options.selectedSectionIds;
  }
  if (options?.sectionOrder?.length) {
    body.section_order = options.sectionOrder;
  }
  const response = await apiRequest("/game-rule/kb/generate/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionApiDetail>;
  return mapSessionDetail(json.data);
};

export const getGenerationSession = async (
  sessionId: string,
): Promise<KbGenerationSessionDetail> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionApiDetail>;
  return mapSessionDetail(json.data);
};

export const patchGenerationSession = async (
  sessionId: string,
  patch: KbGenerationSessionPatch,
): Promise<KbGenerationSessionDetail> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patchToApiBody(patch)),
    },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionApiDetail>;
  return mapSessionDetail(json.data);
};

export const listKbSections = async (
  params: ListKbSectionsParams = {},
): Promise<ListKbSectionsResult> => {
  const query = new URLSearchParams();
  const search = params.search?.trim();
  if (search) {
    query.set("search", search);
  }
  query.set("page", String(params.page ?? 1));
  query.set("limit", String(params.limit ?? 5));
  query.set("status", params.status ?? "active");

  const response = await apiRequest(`/game-rule/kb/sections?${query.toString()}`, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbSectionListApiResponse;
  const rows = Array.isArray(json.data) ? json.data : [];
  return {
    items: rows.map(mapSectionListItem),
    total: json.meta?.total ?? rows.length,
    page: json.meta?.page ?? params.page ?? 1,
    limit: json.meta?.limit ?? params.limit ?? 5,
  };
};

export const buildFieldSteps = async (
  selectedSectionIds: readonly string[],
): Promise<readonly FieldStep[]> => {
  const response = await apiRequest("/game-rule/kb/generate/field-steps", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selected_section_ids: selectedSectionIds }),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<{ steps?: readonly FieldStepApiItem[] }>;
  const steps = Array.isArray(json.data?.steps) ? json.data.steps : [];
  return steps.map(mapFieldStep);
};

export const resolveSessionPlaceholders = async (
  sessionId: string,
): Promise<KbGenerationSessionResolvePlaceholdersResult> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}/resolve-placeholders`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<{
    readonly session_id: string;
    readonly status: string;
    readonly sections_resolved?: number;
  }>;
  const data = json.data;
  return {
    sessionId: String(data.session_id),
    status: data.status,
    sectionsResolved: data.sections_resolved ?? 0,
  };
};

export const startSessionAi = async (
  sessionId: string,
  options?: { readonly force?: boolean },
): Promise<KbGenerationSessionAiStartResult> => {
  const force = options?.force ? "?force=true" : "";
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}/ai/start${force}`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionAiStartApi>;
  const data = json.data;
  return {
    sessionId: String(data.session_id),
    aiJobId: String(data.ai_job_id),
    status: data.status,
    totalSections: data.total_sections,
    completedSections: data.completed_sections,
  };
};

export const getSessionAiStatus = async (
  sessionId: string,
): Promise<KbGenerationSessionAiStatus> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}/ai/status`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionAiStatusApi>;
  return mapAiStatus(json.data);
};

type KbGenerationSessionCommitApi = {
  readonly document_id: string;
  readonly file_name: string;
  readonly document_web_url?: string | null;
  readonly section_count: number;
  readonly status: string;
  readonly generation_session_id: string;
};

export const commitGenerationSession = async (
  sessionId: string,
): Promise<KbGenerationSessionCommitResult> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}/commit`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionCommitApi>;
  const data = json.data;
  return {
    documentId: String(data.document_id),
    fileName: data.file_name,
    documentWebUrl: data.document_web_url ?? null,
    sectionCount: data.section_count,
    status: data.status,
    generationSessionId: String(data.generation_session_id),
  };
};

type KbGenerationSessionPreviewApi = {
  readonly docx_base64: string;
  readonly section_count: number;
};

export const previewGenerationSession = async (
  sessionId: string,
  sectionOrder: readonly string[],
): Promise<KbGenerationSessionPreviewResult> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/sessions/${encodeURIComponent(sessionId)}/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        section_order: sectionOrder,
      }),
    },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<KbGenerationSessionPreviewApi>;
  const data = json.data;
  return {
    docxBase64: data.docx_base64,
    sectionCount: data.section_count,
  };
};
